import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api, { getImageUrl } from '../api';
import { productSlug } from '../utils/slug';

const SHIPPING_RATE = 7.99;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
};

export default function Cart() {
  const { items, updateQuantity, removeItem, clearCart, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('cart'); // 'cart' | 'checkout'
  const [ordering, setOrdering] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // { code, discount_pct }
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [isVIP, setIsVIP] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: user?.email || '',
    phone: '', address: '', address2: '', city: '', state: '', zip: '',
  });
  const [errors, setErrors] = useState({});
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);

  // Load saved addresses for logged-in users
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    if (user) {
      api.get('/membership').then(r => {
        if (r.data?.status === 'active') {
          setIsVIP(true);
          setMembership(r.data);
        }
      }).catch(() => {});
      api.get('/addresses').then(r => {
        setSavedAddresses(r.data);
        if (r.data.length > 0) {
          const def = r.data.find(a => a.is_default) || r.data[0];
          setSelectedAddressId(def.id);
          setForm(f => ({
            ...f, first_name: def.first_name, last_name: def.last_name,
            phone: def.phone || '', address: def.address, address2: def.address2 || '',
            city: def.city, state: def.state, zip: def.zip,
          }));
        } else {
          setShowNewForm(true);
        }
      }).catch(() => setShowNewForm(true));
    } else {
      setShowNewForm(true);
    }
  }, [user]);

  const isBirthdayMonth = isVIP && membership?.birthday_month && (new Date().getMonth() + 1) === parseInt(membership.birthday_month);
  const vipDiscount = isBirthdayMonth ? 15 : isVIP ? 10 : 0;
  const effectiveDiscount = coupon ? Math.max(coupon.discount_pct, vipDiscount) : vipDiscount;
  const shipping = (isVIP || subtotal >= 50) ? 0 : SHIPPING_RATE;
  const discountAmt = effectiveDiscount > 0 ? subtotal * (effectiveDiscount / 100) : 0;
  const tax = (subtotal - discountAmt) * 0.08;
  const total = subtotal - discountAmt + shipping + tax;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true); setCouponError('');
    try {
      const r = await api.post('/payments/validate-coupon', { code: couponInput.trim(), user_id: user?.id });
      setCoupon(r.data);
      setCouponError('');
    } catch (err) {
      setCoupon(null);
      setCouponError(err.response?.data?.error || 'Invalid coupon');
    } finally { setCouponLoading(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    if (!form.address.trim()) e.address = 'Required';
    if (!form.city.trim()) e.city = 'Required';
    if (!form.state.trim()) e.state = 'Required';
    if (!form.zip.trim() || !/^\d{5}(-\d{4})?$/.test(form.zip.trim())) e.zip = 'Enter a valid ZIP (e.g. 07076 or 07076-2345)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validate()) return;
    setOrdering(true);
    try {
      const orderItems = items.map(i => ({ product_id: i.product.id, quantity: i.quantity, variant_id: i.variant?.id || null }));
      // Save shipping form to sessionStorage so success page can reference it
      sessionStorage.setItem('sm_checkout_form', JSON.stringify(form));
      const res = await api.post('/payments/create-checkout-session', {
        items: orderItems,
        shipping_address: form,
        user_id: user?.id || null,
        coupon_code: coupon?.code || null,
      });
      // Redirect to Stripe Checkout
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'Payment setup failed. Please try again.');
      setOrdering(false);
    }
  };

  if (orderResult) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: 60, maxWidth: 500, margin: '0 auto' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <h2>Order Placed!</h2>
          <p style={{ margin: '12px 0', color: '#565959' }}>
            Order #{orderResult.id} — Total: ${orderResult.total?.toFixed(2)}
          </p>
          <p style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>
            We'll send you updates as your order is prepared and shipped.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Link to={`/track`} className="btn btn-primary" onClick={() => setForm(f => ({ ...f, order_id: String(orderResult.id) }))}>Track My Order</Link>
            <Link to="/shop" className="btn btn-outline">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="container">
        <div className="empty-state">
          <div className="icon">🛒</div>
          <h2>Your cart is empty</h2>
          <p>Start shopping to add items to your cart.</p>
          <Link to="/shop" className="btn btn-primary" style={{ marginTop: 16 }}>Browse Products</Link>
        </div>
      </div>
    );
  }

  const inputStyle = (field) => ({
    width: '100%', padding: '9px 12px', border: `1.5px solid ${errors[field] ? '#ef4444' : '#ddd'}`,
    borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
  });

  const summaryBox = (
    <div className="cart-summary">
      <h3 style={{ marginBottom: 16 }}>Order Summary</h3>
      {isVIP && (
        <div style={{ background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)', border: '1px solid #a78bfa', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{isBirthdayMonth ? '🎂' : '💎'}</span>
          <span style={{ color: '#4f46e5', fontWeight: 700 }}>
            {isBirthdayMonth ? 'VIP Birthday Month: 15% off + free shipping! 🎉' : 'VIP: 10% off + free shipping applied!'}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
        <span>Subtotal ({items.reduce((s, i) => s + i.quantity, 0)} items)</span>
        <span>${subtotal.toFixed(2)}</span>
      </div>
      {effectiveDiscount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, color: 'var(--success)', fontWeight: 600 }}>
          <span>{isVIP && effectiveDiscount === vipDiscount ? '💎 VIP Discount (10%)' : `Discount (${coupon?.code} -${effectiveDiscount}%)`}</span>
          <span>-${discountAmt.toFixed(2)}</span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
        <span>Shipping</span>
        <span>{shipping === 0 ? <span style={{ color: 'var(--success)' }}>FREE</span> : `$${shipping.toFixed(2)}`}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 14 }}>
        <span>Estimated Tax</span>
        <span>${tax.toFixed(2)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 18, borderTop: '1px solid #ddd', paddingTop: 16, marginBottom: 16 }}>
        <span>Total</span>
        <span>${total.toFixed(2)}</span>
      </div>
      {/* Coupon field */}
      {step === 'checkout' && (
        <div style={{ marginBottom: 16 }}>
          {coupon ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
              <span style={{ color: '#166534', fontWeight: 600 }}>{'\u2713'} {coupon.code} applied &mdash; {coupon.discount_pct}% off!</span>
              <button onClick={() => { setCoupon(null); setCouponInput(''); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>Remove</button>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 13 }}
                  placeholder="Coupon code" value={couponInput}
                  onChange={e => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                />
                <button className="btn btn-outline btn-sm" onClick={applyCoupon} disabled={couponLoading}>
                  {couponLoading ? '...' : 'Apply'}
                </button>
              </div>
              {couponError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>{couponError}</p>}
            </div>
          )}
        </div>
      )}
      {!isVIP && subtotal < 50 && (
        <p style={{ fontSize: 12, color: 'var(--success)', marginBottom: 12, textAlign: 'center' }}>
          Add ${(50 - subtotal).toFixed(2)} more for free shipping!
        </p>
      )}
      {step === 'cart' && (
        <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={() => setStep('checkout')}>
          Proceed to Checkout &rarr;
        </button>
      )}
      {step === 'checkout' && (
        <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={handlePlaceOrder} disabled={ordering}>
          {ordering ? 'Redirecting to payment\u2026' : `Pay with Card \u2014 $${total.toFixed(2)}`}
        </button>
      )}
      {!user && <p style={{ fontSize: 12, color: '#565959', marginTop: 8, textAlign: 'center' }}>No account needed — checkout as guest</p>}
    </div>
  );

  return (
    <div className="container">
      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, fontSize: 14 }}>
        <span style={{ fontWeight: step === 'cart' ? 700 : 400, color: step === 'cart' ? 'var(--primary)' : '#6b7280' }}>1. Cart</span>
        <span style={{ color: '#d1d5db' }}>›</span>
        <span style={{ fontWeight: step === 'checkout' ? 700 : 400, color: step === 'checkout' ? 'var(--primary)' : '#6b7280' }}>2. Shipping</span>
        <span style={{ color: '#d1d5db' }}>›</span>
        <span style={{ color: '#6b7280' }}>3. Payment</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
        {step === 'cart' ? (
          /* ── Cart items ── */
          <div>
            {items.map(item => {
              const thumbnail = item.product.images && item.product.images.length > 0
                  ? item.product.images[0].image_url
                  : item.product.image_url;
              const imgUrl = getImageUrl(thumbnail);
              return (
                <div key={item.product.id} className="cart-item">
                  {imgUrl ? (
                    <img src={imgUrl} alt={item.product.name} className="cart-item-img" />
                  ) : (
                    <div className="cart-item-img" style={{ background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>📦</div>
                  )}
                  <div className="cart-item-info">
                    <Link to={`/product/${productSlug(item.product.name, item.product.id)}`} style={{ fontWeight: 600 }}>{item.product.name}</Link>
                    {item.variant && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {Object.entries(item.variant.attributes).map(([k,v]) => `${k}: ${v}`).join(' | ')}
                      </div>
                    )}
                    <div style={{ fontSize: 18, fontWeight: 700, margin: '8px 0' }}>${(item.product.price * item.quantity).toFixed(2)}</div>
                    <div className="qty-control">
                      <button onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.variant?.id)}>-</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product.id, Math.min(item.product.stock_quantity || 99, item.quantity + 1), item.variant?.id)}>+</button>
                    </div>
                    <button
                      onClick={() => removeItem(item.product.id, item.variant?.id)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 13, cursor: 'pointer', padding: '4px 0', fontWeight: 500 }}
                    >
                      &#128465; Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Shipping address form ── */
          <div style={{ background: 'white', padding: 28, borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setStep('cart')}>&larr; Back</button>
              <h2 style={{ margin: 0 }}>Shipping Address</h2>
            </div>

            {/* Saved addresses selector */}
            {user && savedAddresses.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                  {savedAddresses.map(addr => (
                    <label key={addr.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
                      border: `2px solid ${selectedAddressId === addr.id ? 'var(--primary)' : '#e5e7eb'}`,
                      borderRadius: 10, cursor: 'pointer',
                      background: selectedAddressId === addr.id ? '#f5f3ff' : 'white',
                    }}>
                      <input type="radio" name="savedAddr" checked={selectedAddressId === addr.id}
                        onChange={() => {
                          setSelectedAddressId(addr.id);
                          setShowNewForm(false);
                          setForm(f => ({
                            ...f, first_name: addr.first_name, last_name: addr.last_name,
                            phone: addr.phone || '', address: addr.address,
                            address2: addr.address2 || '', city: addr.city,
                            state: addr.state, zip: addr.zip,
                          }));
                        }}
                        style={{ marginTop: 3 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {addr.label} {addr.is_default ? <span style={{ fontSize: 11, background: '#4f46e5', color: 'white', borderRadius: 4, padding: '1px 6px', marginLeft: 6 }}>Default</span> : null}
                        </div>
                        <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>
                          {addr.first_name} {addr.last_name} &bull; {addr.address}{addr.address2 ? `, ${addr.address2}` : ''}, {addr.city}, {addr.state} {addr.zip}
                        </div>
                        {addr.phone && <div style={{ fontSize: 12, color: '#6b7280' }}>{addr.phone}</div>}
                      </div>
                    </label>
                  ))}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    border: `2px solid ${showNewForm ? 'var(--primary)' : '#e5e7eb'}`,
                    borderRadius: 10, cursor: 'pointer',
                    background: showNewForm ? '#f5f3ff' : 'white',
                  }}>
                    <input type="radio" name="savedAddr" checked={showNewForm}
                      onChange={() => { setShowNewForm(true); setSelectedAddressId(null);
                        setForm(f => ({ ...f, first_name:'',last_name:'',phone:'',address:'',address2:'',city:'',state:'',zip:'' }));
                      }} style={{ marginTop: 3 }} />
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--primary)' }}>+ Use a new address</span>
                  </label>
                </div>
              </div>
            )}

            {/* Address form — shown when no saved addresses or "new address" selected */}
            {(showNewForm || !user || savedAddresses.length === 0) && (
            <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>First Name *</label>
                <input style={inputStyle('first_name')} value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="John" />
                {errors.first_name && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.first_name}</span>}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>Last Name *</label>
                <input style={inputStyle('last_name')} value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Doe" />
                {errors.last_name && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.last_name}</span>}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>Email *</label>
                <input style={inputStyle('email')} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="john@example.com" />
                {errors.email && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.email}</span>}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>Phone</label>
                <input style={inputStyle('phone')} value={form.phone} onChange={e => set('phone', formatPhone(e.target.value))} placeholder="(555) 000-0000" inputMode="tel" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>Address *</label>
                <input style={inputStyle('address')} value={form.address} onChange={e => set('address', e.target.value)} placeholder="123 Main St" />
                {errors.address && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.address}</span>}
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>Apartment, suite, etc. (optional)</label>
                <input style={inputStyle('address2')} value={form.address2} onChange={e => set('address2', e.target.value)} placeholder="Apt 4B" />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>City *</label>
                <input style={inputStyle('city')} value={form.city} onChange={e => set('city', e.target.value)} placeholder="New York" />
                {errors.city && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.city}</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>State *</label>
                  <select style={{ ...inputStyle('state'), background: 'white' }} value={form.state} onChange={e => set('state', e.target.value)}>
                    <option value="">Select state…</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.state}</span>}
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 5 }}>ZIP Code *</label>
                  <input style={inputStyle('zip')} value={form.zip} onChange={e => {
                    let v = e.target.value.replace(/[^\d-]/g, '');
                    // Auto-insert dash after 5 digits
                    if (v.length === 6 && !v.includes('-')) v = v.slice(0,5) + '-' + v[5];
                    if (v.length > 10) v = v.slice(0, 10);
                    set('zip', v);
                  }} placeholder="07076 or 07076-2345" maxLength={10} inputMode="numeric" />
                  {errors.zip && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.zip}</span>}
                </div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 20 }}>
              &#128274; Your information is encrypted and secure. Proceed to pay via Stripe.
            </p>
            </>)}
          </div>
        )}

        {summaryBox}
      </div>
    </div>
  );
}

